import React, { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { BookPlus } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { Soft } from "../../Common/Soft";
import { tokens } from "@/theme/tokens";
import { entryVariants, resolveTransition } from "../motion";
import type { PendingReview } from "../../../chat/hooks/useStreamingTurn";

export interface PromoteApprovalCardProps {
  review: PendingReview;
  onDecide: (decision: "approve" | "reject") => void | Promise<void>;
}

/**
 * The approval gate for a `promote_to_knowledge` call (spec 2026-10-05 §3.4):
 * the agent asked to add a file to the knowledge base and nothing runs until
 * the user decides. Approve and reject only, like every gated card — the
 * server runs the call with the arguments it persisted.
 *
 * The filename comes from the server (`review.document`), which names the
 * file only through the promote's own write gate; without it the card shows
 * the bare id the model chose rather than inventing a name.
 */
export function PromoteApprovalCard({ review, onDecide }: PromoteApprovalCardProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<"idle" | "approved" | "rejected">("idle");
  const reduce = useReducedMotion() ?? false;

  const rawId = review.args?.document_id;
  const fileLabel = review.document?.filename ?? `Document ${String(rawId ?? "")}`.trim();
  // The gate failed closed: the tool never ran and a decision would land
  // nowhere, so none is offered.
  const gateFailed = Boolean(review.error);
  const disabled = busy || gateFailed;

  const decide = (decision: "approve" | "reject") => async () => {
    if (disabled) return;
    setBusy(true);
    setError(null);
    try {
      await onDecide(decision);
      setOutcome(decision === "approve" ? "approved" : "rejected");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not record your decision");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div initial="hidden" animate="visible" variants={entryVariants} transition={resolveTransition(reduce)}>
      <Soft
        variant="raised"
        role="region"
        aria-label="Knowledge base approval"
        sx={{
          my: 1.5,
          p: 1.5,
          boxShadow: (t) =>
            `${t.palette.mode === "light" ? tokens.neu.light.raised : tokens.neu.dark.raised}, inset 0 0 0 1px ${alpha(t.palette.primary.main, 0.45)}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1, color: "primary.main" }}>
          <BookPlus size={16} aria-hidden />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Add to knowledge base?
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: "text.primary", wordBreak: "break-all" }}>
          {fileLabel}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
          It will be indexed and searchable in every conversation. The stored file is reused; no copy is made.
        </Typography>

        {gateFailed && (
          <Typography variant="body2" sx={{ color: "error.main", mt: 1 }}>
            {`This approval could not be set up (${review.error}). Nothing was changed — ask again.`}
          </Typography>
        )}

        {outcome === "idle" ? (
          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
            <Button size="small" variant="contained" disabled={disabled} onClick={decide("approve")}>
              Add
            </Button>
            <Button size="small" variant="outlined" disabled={disabled} onClick={decide("reject")}>
              Don&apos;t add
            </Button>
          </Box>
        ) : (
          <Typography
            variant="caption"
            sx={{ color: outcome === "approved" ? "success.main" : "text.secondary", mt: 1, display: "block" }}
          >
            {outcome === "approved" ? "Approved — adding…" : "Not added."}
          </Typography>
        )}

        {error && (
          <Typography variant="caption" sx={{ color: "error.main", display: "block", mt: 0.5 }}>
            {error}
          </Typography>
        )}
      </Soft>
    </motion.div>
  );
}
